import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Badge, Popover, Popconfirm, Spin, message } from 'antd';
import { BellOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import ItemNotificacion from './ItemNotificacion';
import {
  EVENTO_NOTIFICACIONES,
  agruparNotificaciones,
  avisarCambioNotificaciones,
  limpiarNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
  obtenerResumen
} from '../../Utils/notificaciones';

const MAXIMO_EN_MENU = 5;
const INTERVALO_MS = 60000;

/**
 * Notificaciones del menú lateral: las 5 más recientes, agrupadas en "Por
 * atender" (lo que pide una acción de la persona) y "Actualizaciones". Si hay
 * más, un enlace lleva a la lista completa.
 */
const Notificaciones = ({ collapsed }) => {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState({ items: [], unread: 0, pending: 0, total: 0 });
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(() => {
    obtenerResumen()
      .then((resumen) => setDatos(resumen))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    recargar();
    const temporizador = setInterval(recargar, INTERVALO_MS);
    window.addEventListener(EVENTO_NOTIFICACIONES, recargar);
    return () => {
      clearInterval(temporizador);
      window.removeEventListener(EVENTO_NOTIFICACIONES, recargar);
    };
  }, [recargar]);

  const abrirNotificacion = (n) => {
    setAbierto(false);
    if (!n.read) {
      marcarLeida(n.id)
        .then(avisarCambioNotificaciones)
        .catch(() => {});
    }
    if (n.path) navigate(`/${n.path}`);
  };

  const todasLeidas = () =>
    marcarTodasLeidas()
      .then(avisarCambioNotificaciones)
      .catch(() => message.error('No fue posible marcarlas como leídas. Intenta de nuevo.'));

  const limpiar = () =>
    limpiarNotificaciones()
      .then(avisarCambioNotificaciones)
      .catch(() => message.error('No fue posible limpiar las notificaciones. Intenta de nuevo.'));

  const grupos = agruparNotificaciones(datos.items).filter((g) => g.items.length > 0);

  const panel = (
    <div className="notificaciones-panel">
      <div className="notificaciones-cabecera">
        <strong>Notificaciones</strong>
        <span className="notificaciones-acciones">
          <button
            type="button"
            className="notificaciones-accion"
            onClick={todasLeidas}
            disabled={datos.unread === 0}>
            <CheckOutlined /> Marcar leídas
          </button>
          <Popconfirm
            title="¿Limpiar todas las notificaciones?"
            description="Se eliminarán de tu lista."
            okText="Limpiar"
            cancelText="Cancelar"
            onConfirm={limpiar}
            disabled={datos.total === 0}>
            <button type="button" className="notificaciones-accion" disabled={datos.total === 0}>
              <DeleteOutlined /> Limpiar
            </button>
          </Popconfirm>
        </span>
      </div>

      <div className="notificaciones-lista">
        {cargando ? (
          <div className="notificaciones-vacio">
            <Spin size="small" />
          </div>
        ) : grupos.length === 0 ? (
          <p className="notificaciones-vacio">No tienes notificaciones.</p>
        ) : (
          grupos.map((grupo) => (
            <section key={grupo.clave} aria-label={grupo.titulo}>
              <h4 className="notificaciones-grupo">{grupo.titulo}</h4>
              {grupo.items.map((n) => (
                <ItemNotificacion key={n.id} notificacion={n} onAbrir={abrirNotificacion} />
              ))}
            </section>
          ))
        )}
      </div>

      {datos.total > MAXIMO_EN_MENU && (
        <button
          type="button"
          className="notificaciones-mas"
          onClick={() => {
            setAbierto(false);
            navigate('/notifications');
          }}>
          Ver todas ({datos.total})
        </button>
      )}
    </div>
  );

  const etiqueta =
    datos.unread > 0
      ? `Notificaciones, ${datos.unread} sin leer${
          datos.pending > 0 ? `, ${datos.pending} por atender` : ''
        }`
      : 'Notificaciones';

  return (
    <div className={`notificaciones-menu${collapsed ? ' notificaciones-menu--plegado' : ''}`}>
      <Popover
        content={panel}
        trigger="click"
        placement="rightTop"
        arrow={false}
        open={abierto}
        onOpenChange={(valor) => {
          setAbierto(valor);
          if (valor) recargar();
        }}
        overlayClassName="notificaciones-popover">
        <button type="button" className="notificaciones-boton" aria-label={etiqueta}>
          <Badge count={datos.unread} size="small" overflowCount={99} offset={[2, 0]}>
            <BellOutlined className="notificaciones-icono" />
          </Badge>
          {!collapsed && <span className="notificaciones-texto">Notificaciones</span>}
        </button>
      </Popover>
    </div>
  );
};

Notificaciones.propTypes = {
  collapsed: PropTypes.bool
};

export default Notificaciones;
